import React, { useEffect, useState, useContext } from 'react';
import { Button, Grid, TextField, InputAdornment, Alert, Autocomplete, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import { NAME_REGEX, DESCRIPTION_REGEX, PHONE_REGEX, EMAIL_REGEX, CHURCH_CATEGORY_REGEX } from '../../utils/constants';
import SERVER_API_URL from '../../config';
import {
  REQUIRED_FIELD,
  NAME_EXCEPTION,
  DESCRIPTION_EXCEPTION,
  PHONE_EXCEPTION,
  EMAIL_EXCEPTION,
  INTERNAL_SERVER_ERROR,
  CHURCH_CATEGORY_EXCEPTION,
} from '../../utils/exception';
import axiosInstance from '../../api';
import { authContext } from '../../routes';
import { FormProgress } from '../commonForChurches';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';

export default function ChurchRegistrationForm({ setChurchToBeUpdated, setChurchActivatedOn, notification }) {
  const [disabled, setDisabled] = useState(false);
  const { user } = useContext(authContext);

  const handleSubmitNewChurch = async (event) => {
    setDisabled(true);
    event.preventDefault();
    notification.severity = 'error';

    if (
      !formErrorOnDistrictName &&
      !formErrorOnChurchName &&
      !formErrorOnDescription &&
      !formErrorOnType &&
      !formErrorOnPhone &&
      !formErrorOnEmail &&
      !formErrorOnHistory
    ) {
      const { districtName, churchName, description, type, phone, email, history } = event.target.elements;
      let districtNameD = districtName.value;

      if (!districtName.value && user?.role.name === "የአውራጃ አስተዳዳሪ") {
        districtNameD = user.church.district.name;
      }

      try {
        const response = await axiosInstance.post(
          `${SERVER_API_URL}/church/registerOne/${districtNameD}/${user.id}`,
          {
            churchName: churchName.value,
            description: description.value,
            type: type.value,
            phone: phone.value,
            email: email.value,
            history: history.value,
          }
        );

        if (response.status === 201) {
          notification.message = 'አጥቢያ በተሳካ ሁኔታ ተመዝግቧል።';
          notification.severity = 'success';
          setChurchToBeUpdated(response.data);
          setChurchActivatedOn('churchAvatar');
        }
      } catch (e) {
        if (e.response.status === 406) {
          setFormError('በአንድ ወረዳ ስር ያለ የአጥቢያ ስም ልዩ መሆን አለበት!');
          setFormErrorSeverity('error');
        } else if (e.response.status === 400) {
          setFormError(e.response.data.message);
          setFormErrorSeverity('error');
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
          setFormErrorSeverity('error');
        }
      }
    }
    setDisabled(false);
  };

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [districts, setDistricts] = useState(null);
  const [districtNameClicked, setDistrictNameClicked] = useState(false);
  const [formErrorOnDistrictName, setFormErrorOnDistrictName] = useState(false);
  const [helperTextOnDistrictName, setHelperTextOnDistrictName] = useState(null);

  const [churchNameClicked, setChurchNameClicked] = useState(false);
  const [formErrorOnChurchName, setFormErrorOnChurchName] = useState(false);
  const [helperTextOnChurchName, setHelperTextOnChurchName] = useState(null);

  const [descriptionClicked, setDescriptionClicked] = useState(false);
  const [formErrorOnDescription, setFormErrorOnDescription] = useState(false);
  const [helperTextOnDescription, setHelperTextOnDescription] = useState(null);

  const [typeClicked, setTypeClicked] = useState(false);
  const [formErrorOnType, setFormErrorOnType] = useState(false);
  const [helperTextOnType, setHelperTextOnType] = useState(null);

  const [phoneClicked, setPhoneClicked] = useState(false);
  const [formErrorOnPhone, setFormErrorOnPhone] = useState(false);
  const [helperTextOnPhone, setHelperTextOnPhone] = useState(null);

  const [emailClicked, setEmailClicked] = useState(false);
  const [formErrorOnEmail, setFormErrorOnEmail] = useState(false);
  const [helperTextOnEmail, setHelperTextOnEmail] = useState(null);

  const [historyClicked, setHistoryClicked] = useState(false);
  const [formErrorOnHistory, setFormErrorOnHistory] = useState(false);
  const [helperTextOnHistory, setHelperTextOnHistory] = useState(null);

  const getDistricts = async () => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/district/findAll`);

      if (response.status === 200) {
        setDistricts(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  };

  useEffect(() => {
    getDistricts();
  }, []);

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'districtName':
        if (value.length === 0) {
          setDistrictNameClicked(false);
          setFormErrorOnDistrictName(districtNameClicked && value.length === 0);
          setHelperTextOnDistrictName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnDistrictName(true);
          setHelperTextOnDistrictName(NAME_EXCEPTION);
        } else if (!districts || !districts.some((obj) => obj.name === value)) {
          setFormErrorOnDistrictName(true);
          setHelperTextOnDistrictName('የወረዳው ስም ቀደም ሲል ከተመዘገቡ ወረዳዎች ውስጥ አንዱ መሆን አለበት።');
        } else {
          setHelperTextOnDistrictName(null);
          setFormErrorOnDistrictName(false);
        }
        break;
      case 'churchName':
        if (value.length === 0) {
          setChurchNameClicked(false);
          setFormErrorOnChurchName(churchNameClicked && value.length === 0);
          setHelperTextOnChurchName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnChurchName(true);
          setHelperTextOnChurchName(NAME_EXCEPTION);
        } else {
          setHelperTextOnChurchName(null);
          setFormErrorOnChurchName(false);
        }
        break;
      case 'description':
        if (value.length === 0) {
          setDescriptionClicked(false);
          setFormErrorOnDescription(descriptionClicked && value.length === 0);
          setHelperTextOnDescription(REQUIRED_FIELD);
        } else if (!DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnDescription(true);
          setHelperTextOnDescription(DESCRIPTION_EXCEPTION);
        } else {
          setHelperTextOnDescription(null);
          setFormErrorOnDescription(false);
        }
        break;
      case 'type':
        if (value.length === 0) {
          setTypeClicked(false);
          setFormErrorOnType(typeClicked && value.length === 0);
          setHelperTextOnType(REQUIRED_FIELD);
        } else if (!CHURCH_CATEGORY_REGEX.test(value)) {
          setFormErrorOnType(true);
          setHelperTextOnType(CHURCH_CATEGORY_EXCEPTION);
        } else {
          setHelperTextOnType(null);
          setFormErrorOnType(false);
        }
        break;
      case 'phone':
        if (value.length === 0) {
          setPhoneClicked(false);
          setFormErrorOnPhone(phoneClicked && value.length === 0);
          setHelperTextOnPhone(REQUIRED_FIELD);
        } else if (!PHONE_REGEX.test(value)) {
          setFormErrorOnPhone(true);
          setHelperTextOnPhone(PHONE_EXCEPTION);
        } else {
          setHelperTextOnPhone(null);
          setFormErrorOnPhone(false);
        }
        break;
      case 'email':
        if (value.length === 0) {
          setEmailClicked(false);
          setFormErrorOnEmail(emailClicked && value.length === 0);
          setHelperTextOnEmail(REQUIRED_FIELD);
        } else if (!EMAIL_REGEX.test(value)) {
          setFormErrorOnEmail(true);
          setHelperTextOnEmail(EMAIL_EXCEPTION);
        } else {
          setHelperTextOnEmail(null);
          setFormErrorOnEmail(false);
        }
        break;
      case 'history':
        if (value.length === 0) {
          setHistoryClicked(false);
          setFormErrorOnHistory(historyClicked && value.length === 0);
          setHelperTextOnHistory(REQUIRED_FIELD);
        } else {
          setHelperTextOnHistory(null);
          setFormErrorOnHistory(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    const { name } = e.target;
    switch (name) {
      case 'districtName':
        setFormErrorOnDistrictName(false);
        setHelperTextOnDistrictName(null);
        break;
      case 'churchName':
        setFormErrorOnChurchName(false);
        setHelperTextOnChurchName(null);
        break;
      case 'description':
        setFormErrorOnDescription(false);
        setHelperTextOnDescription(null);
        break;
      case 'type':
        setFormErrorOnType(false);
        setHelperTextOnType(null);
        break;
      case 'phone':
        setFormErrorOnPhone(false);
        setHelperTextOnPhone(null);
        break;
      case 'email':
        setFormErrorOnEmail(false);
        setHelperTextOnEmail(null);
        break;
      case 'history':
        setFormErrorOnHistory(false);
        setHelperTextOnHistory(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setFormError(null);
    setFormErrorSeverity('error');

    setDistrictNameClicked(false);
    setFormErrorOnDistrictName(false);
    setHelperTextOnDistrictName(null);

    setChurchNameClicked(false);
    setFormErrorOnChurchName(false);
    setHelperTextOnChurchName(null);

    setDescriptionClicked(false);
    setFormErrorOnDescription(false);
    setHelperTextOnDescription(null);

    setTypeClicked(false);
    setFormErrorOnType(false);
    setHelperTextOnType(null);

    setPhoneClicked(false);
    setFormErrorOnPhone(false);
    setHelperTextOnPhone(null);

    setEmailClicked(false);
    setFormErrorOnEmail(false);
    setHelperTextOnEmail(null);

    setHistoryClicked(false);
    setFormErrorOnHistory(false);
    setHelperTextOnHistory(null);
  };
  return (
    <StyledCardNoHeight
      sx={{ padding: { xs: 1, sm: 10 } }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmitNewChurch}
    >
      <FormProgress activeStep={0} />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          {formError && (
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={6} breakLine>
          <Autocomplete
            freeSolo
            disabled={user && user.role.name === 'የአውራጃ አስተዳዳሪ'}
            defaultValue={user && user.church.district.name !== 'diocese' ? user.church.district.name : ''}
            options={
              districts && districts.length >= 1
                ? districts.map((district) => district.name)
                : ['መጀመሪያ ወረዳዎችን መመዝገብ አለቦት።']
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={formErrorOnDistrictName}
                id="districtName" 
                name="districtName"
                label="የወረዳ ስም"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnDistrictName ? helperTextOnDistrictName : null}
                onFocus={() => setDistrictNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnChurchName}
            id="churchName"
            name="churchName"
            label="የአጥቢያ ስም"
            fullWidth
            // variant="standard"
            onChange={handleFormValueChange}
            helperText={formErrorOnChurchName ? helperTextOnChurchName : null}
            onFocus={() => setChurchNameClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              <Autocomplete
                freeSolo
                options={['ገዳም', 'አጥቢያ', 'ካቴድራል']}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={formErrorOnType}
                    id="type"
                    name="type"
                    label="ዓይነት"
                    fullWidth
                    // variant="standard"
                    onChange={handleFormValueChange}
                    helperText={formErrorOnType ? helperTextOnType : null}
                    onFocus={() => setTypeClicked(true)}
                    onBlur={handleFormValueBlur}
                  />
                )}
              />
            </Grid>{' '}
            <Grid item xs={12} sm={12}>
              <TextField
                required
                error={formErrorOnPhone}
                id="phone"
                name="phone"
                label="ስልክ"
                fullWidth
                // variant="standard"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+251</InputAdornment>,
                }}
                onChange={handleFormValueChange}
                helperText={formErrorOnPhone ? helperTextOnPhone : null}
                onFocus={() => setPhoneClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>{' '}
            <Grid item xs={12} sm={12}>
              <TextField
                required
                error={formErrorOnEmail}
                id="email"
                name="email"
                placeholder="example@gmail.com"
                label="ኢሜይል"
                fullWidth
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnEmail ? helperTextOnEmail : null}
                onFocus={() => setEmailClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnDescription}
            id="description"
            name="description"
            label="መግለጫ"
            fullWidth
            multiline
            rows={8}
            onChange={handleFormValueChange}
            helperText={formErrorOnDescription ? helperTextOnDescription : null}
            onFocus={() => setDescriptionClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            error={formErrorOnHistory}
            id="history"
            name="history"
            label="አጭር ታሪክ"
            fullWidth
            multiline
            rows={7}
            onChange={handleFormValueChange}
            helperText={formErrorOnHistory ? helperTextOnHistory : null}
            onFocus={() => setHistoryClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', m: 2, mb: 0 }}>
          <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
            <RefreshIcon />
            ቅጽ ዳግም አስጀምር
          </Button>
          <Button
            variant="contained"
            type="submit"
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
          >
            {disabled && <CircularProgress style={{ width: '20px', height: '20px' }} />}
            ቅጽ አስገባ
          </Button>
        </Grid>
      </Grid>
    </StyledCardNoHeight>
  );
}

ChurchRegistrationForm.propTypes = {
  setChurchToBeUpdated: PropTypes.func.isRequired,
  setChurchActivatedOn: PropTypes.func.isRequired,
  notification: PropTypes.shape({
    severity: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
  }).isRequired,
};
